import { 
  Box, 
  Typography, 
  Grid, 
  Chip, 
  Card, 
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ANNOUNCEMENT_TYPES } from '../../constants/announcementConstants';
import type { Announcement } from '../../types/announcementTypes';

interface AnnouncementInfoProps {
  announcement: Announcement;
}

export const AnnouncementInfo: React.FC<AnnouncementInfoProps> = ({ announcement }) => {
  const getTypeLabel = (type: string) => {
    return ANNOUNCEMENT_TYPES.find(t => t.value === type)?.label || type;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'ALL': return 'primary';
      case 'PER_APARTMENT': return 'secondary';
      case 'PER_RESIDENT': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  // Validações de segurança
  if (!announcement) {
    return <Typography>Carregando...</Typography>;
  }

  return (
    <Box>
      {/* Informações Básicas */}
      <Card sx={{ mb: 3, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#101E36' }}>
            Informações Básicas
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Título
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {announcement.title}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Tipo
              </Typography>
              <Chip 
                label={getTypeLabel(announcement.type)} 
                color={getTypeColor(announcement.type) as any}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Descrição
              </Typography>
              <Typography variant="body1">
                {announcement.description}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Status de Envio
              </Typography>
              <Chip 
                label={announcement.shoot ? 'Enviado' : 'Não Enviado'} 
                color={announcement.shoot ? 'success' : 'default'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Informações do Tenant */}
      {announcement.tenant && (
        <Card sx={{ mb: 3, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#101E36' }}>
              Tenant
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Nome
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {announcement.tenant.fantasyName || announcement.tenant.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Documento
                </Typography>
                <Typography variant="body1">
                  {announcement.tenant.document || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Email
                </Typography>
                <Typography variant="body1">
                  {announcement.tenant.email || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                  Telefone
                </Typography>
                <Typography variant="body1">
                  {announcement.tenant.phoneCode && announcement.tenant.phoneNumber 
                    ? `(${announcement.tenant.phoneCode}) ${announcement.tenant.phoneNumber}`
                    : '-'
                  }
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Modelos Vinculados */}
      {announcement.models && announcement.models.length > 0 && (
        <Card sx={{ mb: 3, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#101E36' }}>
              Modelos Vinculados ({announcement.models.length})
            </Typography>
            <List>
              {announcement.models.map((model, index) => (
                <div key={model.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={model.name || 'Nome não informado'}
                      secondary={`${model.model_id ? `ID: ${model.model_id}` : ''}`}
                    />
                  </ListItem>
                  {index < announcement.models.length - 1 && <Divider />}
                </div>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Informações de Sistema */}
      <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#101E36' }}>
            Informações do Sistema
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Criado em
              </Typography>
              <Typography variant="body1">
                {announcement.createdAt ? formatDate(announcement.createdAt) : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Atualizado em
              </Typography>
              <Typography variant="body1">
                {announcement.updatedAt ? formatDate(announcement.updatedAt) : '-'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};